import type { ConversationPlacement, CrowdexAssistant } from '@crowdex/agent-contracts/api';
import type { AgentTerminalDisplay } from '@/components/desktop-panel-primitives';
import type { MentionTarget } from '@/components/desktop-side-panel';
import type { SidebarThread } from '@/lib/sidebar-threads';

/** Where the conversations are filed: the assistants, each conversation's placement. */
export type ConversationDirectory = {
  projects: readonly Pick<CrowdexAssistant, 'id' | 'name'>[];
  placements: Readonly<Record<string, ConversationPlacement | undefined>>;
};

/**
 * What a conversation's @ row says of where it is: its assistant ("Website redesign"), and
 * nothing outside any or where the current conversation is too. Never a folder's name or an id:
 * those mean nothing beside a conversation's title.
 */
export function conversationPlaceLabel(
  directory: ConversationDirectory,
  conversationId: string,
  currentId: string | null,
  placement: ConversationPlacement | undefined = directory.placements[conversationId],
): string {
  const current = currentId === null ? undefined : directory.placements[currentId];
  const projectId = placement?.projectId ?? null;
  if (projectId === null || projectId === (current?.projectId ?? null)) return '';
  return directory.projects.find((project) => project.id === projectId)?.name ?? '';
}

/**
 * Assemble the `@`-mention targets offered by the composer: the panel-provided
 * targets (files, browser selections, etc.), the agent's live terminals, and the
 * other conversations in the sidebar, each said by its assistant. Extracted from the
 * inline `useMemo` in `DesktopShell` so the mapping is a pure, unit-testable transform.
 */
export function buildComposerContextTargets(input: {
  panelMentionTargets: readonly MentionTarget[];
  visibleAgentTerminals: readonly AgentTerminalDisplay[];
  sidebarThreads: readonly SidebarThread[];
  /** Where the conversations are filed, to say each one's place (none: nothing said). */
  directory?: ConversationDirectory;
  /** The conversation the composer writes in. */
  currentConversationId?: string | null;
}): readonly MentionTarget[] {
  const { directory } = input;
  return [
    ...input.panelMentionTargets,
    ...input.visibleAgentTerminals.map((terminal) => ({
      id: terminal.id,
      kind: 'terminal' as const,
      label: terminal.command,
      detail: terminal.running ? 'Agent terminal · running' : 'Agent terminal',
    })),
    ...input.sidebarThreads.map((thread) => ({
      id: thread.id,
      kind: 'chat' as const,
      label: thread.title,
      detail:
        directory === undefined
          ? ''
          : conversationPlaceLabel(
              directory,
              thread.id,
              input.currentConversationId ?? null,
              thread.placement ?? directory.placements[thread.id],
            ),
    })),
  ];
}
